import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '../components/WalletContext';
import { web, TransactionType } from '@klever/sdk-web';
import { FaArrowLeft, FaSpinner } from 'react-icons/fa';
import '../styles/TokenDetailsPage.css';

const TokenDetailsPage = () => {
  const { assetId } = useParams();
  const { walletAddress, useKdaFee, selectedKda } = useWallet();
  const navigate = useNavigate();
  
  const [tokenDetails, setTokenDetails] = useState(null);
  const [balance, setBalance] = useState('0');
  const [stakedAmount, setStakedAmount] = useState('0');
  const [rewards, setRewards] = useState([]);
  const [unfreezingAmount, setUnfreezingAmount] = useState('0');
  const [stakedEpoch, setStakedEpoch] = useState(null);
  const [currentEpoch, setCurrentEpoch] = useState(null);
  const [availableEpoch, setAvailableEpoch] = useState('100000000');
  const [minEpochsToClaim, setMinEpochsToClaim] = useState(0);
  const [minEpochsToUnstake, setMinEpochsToUnstake] = useState(0);
  const [minEpochsToWithdraw, setMinEpochsToWithdraw] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState('');
  
  // Modal states
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isStakeModalOpen, setIsStakeModalOpen] = useState(false);
  const [isUnstakeModalOpen, setIsUnstakeModalOpen] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  
  // Form states
  const [sendAmount, setSendAmount] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [stakeAmount, setStakeAmount] = useState('');

  const adjustValue = (value, precision) => {
    const number = value / Math.pow(10, precision);
    return number.toLocaleString('en-US', { 
      minimumFractionDigits: 4, 
      maximumFractionDigits: 4 
    });
  };

  const getLatestAPR = (aprEntries) => {
    if (!aprEntries || aprEntries.length === 0) return null;
    const latest = aprEntries.reduce((prev, current) => 
      (prev.timestamp > current.timestamp ? prev : current)
    );
    return (latest.value / 100).toFixed(2);
  };

  const fetchTokenPrecision = async (assetId) => {
    try {
      for (let page = 1; page <= 2; page++) {
        const url = `https://api.mainnet.klever.org/v1.0/assets/list?page=${page}&limit=100`;
        const response = await fetch(url);
        const data = await response.json();
        const token = data.data.assets.find(asset => asset.assetId === assetId);
        if (token) return token.precision;
      }
      return 6;
    } catch (error) {
      console.error(`Error fetching precision for ${assetId}:`, error);
      return 6;
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch token details
      let token = null;
      for (let page = 1; page <= 2; page++) {
        const url = `https://api.mainnet.klever.org/v1.0/assets/list?page=${page}&limit=100`;
        const response = await fetch(url);
        const data = await response.json();
        token = data.data.assets.find(asset => asset.assetId === assetId);
        if (token) break;
      }
      
      if (!token) throw new Error('Token not found');
      setTokenDetails(token);
      setMinEpochsToClaim(token.staking.minEpochsToClaim || 0);
      setMinEpochsToUnstake(token.staking.minEpochsToUnstake || 0);
      setMinEpochsToWithdraw(token.staking.minEpochsToWithdraw || 0);

      // Fetch balance and staking info
      const balanceResponse = await fetch(
        `https://api.mainnet.klever.org/v1.0/address/${walletAddress}`
      );
      const balanceData = await balanceResponse.json();
      const assetData = balanceData.data.account.assets[assetId] || {};
      
      setBalance(adjustValue(assetData.balance || 0, token.precision));
      setStakedAmount(adjustValue(assetData.frozenBalance || 0, token.precision));
      
      if (assetData.buckets && assetData.buckets.length > 0) {
        setStakedEpoch(assetData.buckets[0].stakedEpoch);
      }

      // Fetch rewards
      const rewardsResponse = await fetch(
        `https://node.mainnet.klever.org/address/${walletAddress}/allowance?asset=${assetId}`
      );
      const rewardsData = await rewardsResponse.json();
      const allRewards = rewardsData.data.allStakingRewards || {};
      
      const formattedRewards = await Promise.all(
        Object.entries(allRewards).map(async ([rewardAssetId, rewardAmount]) => {
          const precision = await fetchTokenPrecision(rewardAssetId);
          const logoResponse = await fetch(
            `https://api.mainnet.klever.org/v1.0/assets/${rewardAssetId}`
          );
          const logoData = await logoResponse.json();
          return {
            assetId: rewardAssetId,
            amount: adjustValue(rewardAmount, precision),
            rawAmount: rewardAmount,
            precision,
            logo: logoData.data.asset.logo || null,
          };
        })
      );
      setRewards(formattedRewards);

      // Fetch current epoch
      const epochResponse = await fetch('https://api.mainnet.klever.org/v1.0/block/list');
      const epochData = await epochResponse.json();
      setCurrentEpoch(epochData.data.blocks[0].epoch);

      // Fetch unfreezing amount
      const unfreezeResponse = await fetch(
        `https://api.mainnet.klever.org/v1.0/transaction/list?type=5&asset=${assetId}&fromAddress=${walletAddress}`
      );
      const unfreezeData = await unfreezeResponse.json();
      const latestUnfreezeTx = unfreezeData.data.transactions.find(
        tx => tx.status === "success"
      );
      
      if (latestUnfreezeTx) {
        const unfreezeReceipt = latestUnfreezeTx.receipts.find(
          receipt => receipt.typeString === "Unfreeze"
        );
        
        if (unfreezeReceipt) {
          setAvailableEpoch(unfreezeReceipt.availableEpoch);
          const unfreezeValue = adjustValue(unfreezeReceipt.value, token.precision);
          
          const withdrawResponse = await fetch(
            `https://api.mainnet.klever.org/v1.0/transaction/list?type=8&asset=${assetId}&fromAddress=${walletAddress}`
          );
          const withdrawData = await withdrawResponse.json();
          const latestWithdrawTx = withdrawData.data.transactions.find(
            tx => tx.status === "success"
          );
          
          const unfreezeTs = latestUnfreezeTx.timestamp;
          const withdrawTs = latestWithdrawTx ? latestWithdrawTx.timestamp : 0;
          setUnfreezingAmount(unfreezeTs > withdrawTs ? unfreezeValue : '0');
        }
      }
    } catch (err) {
      console.error('Error fetching token data:', err);
      setError('Failed to load token details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (walletAddress && assetId) {
      fetchAllData();
      const intervalId = setInterval(fetchAllData, 30000);
      return () => clearInterval(intervalId);
    }
  }, [walletAddress, assetId]);

  const handleSendConfirm = async () => {
    if (!sendAmount || parseFloat(sendAmount) <= 0 || !recipientAddress) {
      setTransactionStatus('Invalid amount or recipient address.');
      return;
    }

    setIsLoading(true);
    const payload = {
      receiver: recipientAddress,
      kda: assetId,
      amount: parseFloat(sendAmount.replace(/,/g, '')) * Math.pow(10, tokenDetails.precision),
    };
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(
        [{ payload, type: TransactionType.Transfer }], 
        undefined, 
        txOptions
      );
      const signedTx = await web.signTransaction(unsignedTx);
      await web.broadcastTransactions([signedTx]);
      
      setTransactionStatus('Transaction sent successfully!');
      setTimeout(() => {
        setIsSendModalOpen(false);
        setTransactionStatus('');
        setSendAmount('');
        setRecipientAddress('');
        fetchAllData();
      }, 2000);
    } catch (err) {
      console.error('Send transaction failed:', err);
      setTransactionStatus('Transaction failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStakeConfirm = async () => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      setTransactionStatus('Invalid stake amount.');
      return;
    }

    setIsLoading(true);
    const payload = {
      kda: assetId,
      amount: parseFloat(stakeAmount.replace(/,/g, '')) * Math.pow(10, tokenDetails.precision),
    };
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(
        [{ payload, type: TransactionType.Freeze }], 
        undefined, 
        txOptions
      );
      const signedTx = await web.signTransaction(unsignedTx);
      await web.broadcastTransactions([signedTx]);
      
      setTransactionStatus('Staking successful!');
      setTimeout(() => {
        setIsStakeModalOpen(false);
        setTransactionStatus('');
        setStakeAmount('');
        fetchAllData();
      }, 2000);
    } catch (err) {
      console.error('Stake transaction failed:', err);
      setTransactionStatus('Transaction failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnstakeConfirm = async () => {
    setIsLoading(true);
    const payload = { assetId, bucketID: assetId };
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(
        [{ payload, type: TransactionType.Unfreeze }], 
        undefined, 
        txOptions
      );
      const signedTx = await web.signTransaction(unsignedTx);
      await web.broadcastTransactions([signedTx]);
      
      setTransactionStatus('Unstaking successful!');
      setTimeout(() => {
        setIsUnstakeModalOpen(false);
        setTransactionStatus('');
        fetchAllData();
      }, 2000);
    } catch (err) {
      console.error('Unstake transaction failed:', err);
      setTransactionStatus('Transaction failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClaimConfirm = async () => {
    setIsLoading(true);
    const payload = { claimType: 0, id: assetId };
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(
        [{ payload, type: TransactionType.Claim }], 
        undefined, 
        txOptions
      );
      const signedTx = await web.signTransaction(unsignedTx);
      await web.broadcastTransactions([signedTx]);
      
      setTransactionStatus('Rewards claimed successfully!');
      setTimeout(() => {
        setIsClaimModalOpen(false);
        setTransactionStatus('');
        fetchAllData();
      }, 2000);
    } catch (err) {
      console.error('Claim transaction failed:', err);
      setTransactionStatus('Transaction failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawConfirm = async () => {
    setIsLoading(true);
    const payload = { withdrawType: 0, assetId, currencyID: assetId };
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(
        [{ payload, type: TransactionType.Withdraw }], 
        undefined, 
        txOptions
      );
      const signedTx = await web.signTransaction(unsignedTx);
      await web.broadcastTransactions([signedTx]);
      
      setTransactionStatus('Withdrawal successful!');
      setTimeout(() => {
        setIsWithdrawModalOpen(false);
        setTransactionStatus('');
        fetchAllData();
      }, 2000);
    } catch (err) {
      console.error('Withdraw transaction failed:', err);
      setTransactionStatus('Transaction failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const canClaim = stakedEpoch !== null && currentEpoch !== null && 
                   (currentEpoch - stakedEpoch >= minEpochsToClaim);
  
  const canUnstake = stakedEpoch !== null && currentEpoch !== null && 
                     (currentEpoch - stakedEpoch >= minEpochsToUnstake);
  
  const canWithdraw = currentEpoch !== null && 
                      (currentEpoch >= parseInt(availableEpoch)) && 
                      parseFloat(unfreezingAmount.replace(/,/g, '')) > 0;

  if (isLoading && !tokenDetails) {
    return (
      <div className="token-details-container">
        <div className="token-details-loading">
          <FaSpinner className="token-details-loading-spinner" />
          <p>Loading token details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-details-container">
        <div className="token-details-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="token-details-container">
      <button 
        className="token-details-back-button" 
        onClick={() => navigate('/wallet/tokens')}
      >
        <FaArrowLeft /> Back to Tokens
      </button>

      {tokenDetails && (
        <>
          <div className="token-details-header">
            {tokenDetails.logo && (
              <img
                src={tokenDetails.logo}
                alt={tokenDetails.name}
                className="token-details-main-token-logo"
              />
            )}
            <div className="token-details-header-info">
              <h1 className="token-details-page-title">{tokenDetails.name}</h1>
              <p className="token-details-ticker">{tokenDetails.ticker}</p>
            </div>
          </div>

          <div className="token-details-sections">
            {/* Token Information Card */}
            <div className="token-details-card">
              <h3>Token Information</h3>
              <div className="token-details-details-grid">
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Asset ID</span>
                  <span className="token-details-detail-value">{tokenDetails.assetId}</span>
                </div>
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Type</span>
                  <span className="token-details-detail-value">{tokenDetails.assetType}</span>
                </div>
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Precision</span>
                  <span className="token-details-detail-value">{tokenDetails.precision}</span>
                </div>
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Total Supply</span>
                  <span className="token-details-detail-value">
                    {(tokenDetails.circulatingSupply / Math.pow(10, tokenDetails.precision)).toLocaleString()}
                  </span>
                </div>
                {getLatestAPR(tokenDetails.staking?.apr) && (
                  <div className="token-details-detail-item">
                    <span className="token-details-detail-label">APR</span>
                    <span className="token-details-detail-value">
                      {getLatestAPR(tokenDetails.staking.apr)}%
                    </span>
                  </div>
                )}
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Min Epochs to Claim</span>
                  <span className="token-details-detail-value">{minEpochsToClaim}</span>
                </div>
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Min Epochs to Unstake</span>
                  <span className="token-details-detail-value">{minEpochsToUnstake}</span>
                </div>
                <div className="token-details-detail-item">
                  <span className="token-details-detail-label">Min Epochs to Withdraw</span>
                  <span className="token-details-detail-value">{minEpochsToWithdraw}</span>
                </div>
              </div>
            </div>

            {/* User Balance & Actions Card */}
            <div className="token-details-user-info">
              <h3 className="token-details-user-info-title">Your Holdings</h3>
              <div className="token-details-user-details-grid">
                {/* Available Balance */}
                <div className="token-details-user-detail">
                  <span className="token-details-detail-label">Available Balance</span>
                  <span className="token-details-detail-value">{balance}</span>
                  <div className="token-details-user-detail-buttons">
                    <button onClick={() => setIsSendModalOpen(true)}>
                      <span>Send</span>
                    </button>
                    <button onClick={() => setIsStakeModalOpen(true)}>
                      <span>Freeze</span>
                    </button>
                  </div>
                </div>

                {/* Staked Amount */}
                <div className="token-details-user-detail">
                  <span className="token-details-detail-label">Staked Amount</span>
                  <span className="token-details-detail-value">{stakedAmount}</span>
                  <div className="token-details-user-detail-buttons">
                    <button 
                      onClick={() => setIsUnstakeModalOpen(true)} 
                      disabled={!canUnstake}
                    >
                      <span>Unfreeze</span>
                    </button>
                  </div>
                </div>

                {/* Unfreezing Amount */}
                <div className="token-details-user-detail">
                  <span className="token-details-detail-label">Unfreezing</span>
                  <span className="token-details-detail-value">{unfreezingAmount}</span>
                  <div className="token-details-user-detail-buttons">
                    <button 
                      onClick={() => setIsWithdrawModalOpen(true)} 
                      disabled={!canWithdraw}
                    >
                      <span>Withdraw</span>
                    </button>
                  </div>
                </div>

                {/* Rewards */}
                <div className="token-details-user-detail">
                  <span className="token-details-detail-label">Staking Rewards</span>
                  {rewards.length > 0 ? (
                    <div className="token-details-rewards-list">
                      {rewards.map((reward, index) => (
                        <div key={index} className="token-details-reward-item">
                          {reward.logo && (
                            <img
                              src={reward.logo}
                              alt={reward.assetId}
                              className="token-details-reward-logo"
                            />
                          )}
                          <span className="token-details-reward-value">
                            {reward.amount} {reward.assetId}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="token-details-no-rewards">No rewards available</div>
                  )}
                  <div className="token-details-rewards-buttons">
                    <button 
                      onClick={() => setIsClaimModalOpen(true)} 
                      disabled={!canClaim || rewards.length === 0}
                    >
                      <span>Claim Rewards</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Send Modal */}
          {isSendModalOpen && (
            <div className="token-details-action-modal" onClick={() => setIsSendModalOpen(false)}>
              <div className="token-details-action-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="token-details-modal-close" onClick={() => setIsSendModalOpen(false)}>
                  ×
                </button>
                <h2>Send {tokenDetails.ticker}</h2>
                <div className="token-details-modal-input-group">
                  <label>Amount</label>
                  <input
                    type="number"
                    placeholder="Enter amount"
                    value={sendAmount}
                    onChange={(e) => setSendAmount(e.target.value)}
                  />
                  <span className="token-details-modal-info-text">Available: {balance}</span>
                </div>
                <div className="token-details-modal-input-group">
                  <label>Recipient Address</label>
                  <input
                    type="text"
                    placeholder="Enter recipient address"
                    value={recipientAddress}
                    onChange={(e) => setRecipientAddress(e.target.value)}
                  />
                </div>
                <div className="token-details-action-modal-actions">
                  <button onClick={handleSendConfirm} disabled={isLoading}>
                    {isLoading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button onClick={() => setIsSendModalOpen(false)} disabled={isLoading}>
                    Cancel
                  </button>
                </div>
                {transactionStatus && (
                  <div className={`token-details-transaction-status ${transactionStatus.includes('failed') ? 'error' : 'success'}`}>
                    {transactionStatus}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stake Modal */}
          {isStakeModalOpen && (
            <div className="token-details-action-modal" onClick={() => setIsStakeModalOpen(false)}>
              <div className="token-details-action-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="token-details-modal-close" onClick={() => setIsStakeModalOpen(false)}>
                  ×
                </button>
                <h2>Freeze {tokenDetails.ticker}</h2>
                <div className="token-details-modal-input-group">
                  <label>Amount to Freeze</label>
                  <input
                    type="number"
                    placeholder="Enter amount"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                  />
                  <span className="token-details-modal-info-text">Available: {balance}</span>
                </div>
                <div className="token-details-action-modal-actions">
                  <button onClick={handleStakeConfirm} disabled={isLoading}>
                    {isLoading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button onClick={() => setIsStakeModalOpen(false)} disabled={isLoading}>
                    Cancel
                  </button>
                </div>
                {transactionStatus && (
                  <div className={`token-details-transaction-status ${transactionStatus.includes('failed') ? 'error' : 'success'}`}>
                    {transactionStatus}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Unstake Modal */}
          {isUnstakeModalOpen && (
            <div className="token-details-action-modal" onClick={() => setIsUnstakeModalOpen(false)}>
              <div className="token-details-action-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="token-details-modal-close" onClick={() => setIsUnstakeModalOpen(false)}>
                  ×
                </button>
                <h2>Unfreeze {tokenDetails.ticker}</h2>
                <div className="token-details-confirmation-text">
                  <p>Unfreeze your entire staked balance:</p>
                  <p><strong>{stakedAmount}</strong></p>
                </div>
                <div className="token-details-action-modal-actions">
                  <button onClick={handleUnstakeConfirm} disabled={isLoading}>
                    {isLoading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button onClick={() => setIsUnstakeModalOpen(false)} disabled={isLoading}>
                    Cancel
                  </button>
                </div>
                {transactionStatus && (
                  <div className={`token-details-transaction-status ${transactionStatus.includes('failed') ? 'error' : 'success'}`}>
                    {transactionStatus}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Claim Rewards Modal */}
          {isClaimModalOpen && (
            <div className="token-details-action-modal" onClick={() => setIsClaimModalOpen(false)}>
              <div className="token-details-action-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="token-details-modal-close" onClick={() => setIsClaimModalOpen(false)}>
                  ×
                </button>
                <h2>Claim Rewards</h2>
                <div className="token-details-confirmation-text">
                  <p>Total rewards to claim:</p>
                  {rewards.map((reward, index) => (
                    <p key={index}>
                      <strong>{reward.amount} {reward.assetId}</strong>
                      {reward.logo && (
                        <img
                          src={reward.logo}
                          alt={`${reward.assetId} logo`}
                          className="token-details-modal-reward-logo"
                        />
                      )}
                    </p>
                  ))}
                </div>
                <div className="token-details-action-modal-actions">
                  <button onClick={handleClaimConfirm} disabled={isLoading}>
                    {isLoading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button onClick={() => setIsClaimModalOpen(false)} disabled={isLoading}>
                    Cancel
                  </button>
                </div>
                {transactionStatus && (
                  <div className={`token-details-transaction-status ${transactionStatus.includes('failed') ? 'error' : 'success'}`}>
                    {transactionStatus}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Withdraw Modal */}
          {isWithdrawModalOpen && (
            <div className="token-details-action-modal" onClick={() => setIsWithdrawModalOpen(false)}>
              <div className="token-details-action-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="token-details-modal-close" onClick={() => setIsWithdrawModalOpen(false)}>
                  ×
                </button>
                <h2>Withdraw {tokenDetails.ticker}</h2>
                <div className="token-details-confirmation-text">
                  <p>Withdraw your available balance:</p>
                  <p><strong>{unfreezingAmount}</strong></p>
                </div>
                <div className="token-details-action-modal-actions">
                  <button onClick={handleWithdrawConfirm} disabled={isLoading}>
                    {isLoading ? 'Processing...' : 'Confirm'}
                  </button>
                  <button onClick={() => setIsWithdrawModalOpen(false)} disabled={isLoading}>
                    Cancel
                  </button>
                </div>
                {transactionStatus && (
                  <div className={`token-details-transaction-status ${transactionStatus.includes('failed') ? 'error' : 'success'}`}>
                    {transactionStatus}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TokenDetailsPage;